import { useEffect, useState, useCallback, Fragment } from "react";
import { Minus, Plus, Percent, IndianRupee, TrendingUp, ShieldCheck, Crown, ChevronDown, ChevronUp, History, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDateTime, errMsg } from "../../lib/api";
import { StatCard, PageHeader, TableShell, TR, TD, LoadingRows, StatusBadge } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function RootRevenue() {
  const [d, setD] = useState(null);
  const [pct, setPct] = useState(10);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api.get("/root/revenue-share").then((r) => { setD(r.data); setPct(r.data.pct); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <LoadingRows rows={6} />;

  const apply = async (val) => {
    const v = Number(val);
    if (isNaN(v) || v < 0 || v > 100) { toast.error("Enter a valid percentage between 0 and 100"); return; }
    setSaving(true);
    try {
      await api.put("/root/revenue-share", { pct: v });
      toast.success(`Platform share set to ${v}%`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const bump = (delta) => setPct((p) => Math.max(0, Math.min(100, Math.round((Number(p) + delta) * 100) / 100)));

  return (
    <div data-testid="root-revenue">
      <PageHeader title="Revenue Share" subtitle="Platform share of each Super Admin's partner revenue. Partner billing stays unchanged — partners always pay their own per-website amount." />

      <div className="border border-border rounded-md p-6 bg-card mb-8 max-w-2xl" data-testid="revenue-share-settings">
        <p className="overline-label mb-1">Revenue Share Settings</p>
        <p className="text-sm text-muted-foreground mb-4">Current Platform Share: <span className="font-extrabold text-foreground" data-testid="current-pct">{d.pct}%</span></p>
        <div className="flex items-center gap-3">
          <button data-testid="pct-minus" onClick={() => bump(-1)}
            className="h-10 w-10 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"><Minus className="h-4 w-4" /></button>
          <div className="relative w-28">
            <Input data-testid="pct-input" type="number" min="0" max="100" step="0.5" value={pct}
              onChange={(e) => setPct(e.target.value)} className="text-center font-extrabold text-lg pr-7" />
            <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
          <button data-testid="pct-plus" onClick={() => bump(1)}
            className="h-10 w-10 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"><Plus className="h-4 w-4" /></button>
          <Button data-testid="pct-save" disabled={saving || Number(pct) === d.pct} onClick={() => apply(pct)} className="rounded-full font-bold">
            {saving ? "Saving…" : "Apply"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Applies to future calculations only — settled transactions are never recalculated. Computed server-side.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard icon={IndianRupee} label="Total Super Admin Revenue" value={fmtINR(d.totals.sa_revenue)} sub="from partners (MRR)" testid="rev-sa-total" />
        <StatCard icon={Percent} label="Root Revenue Share" value={fmtINR(d.totals.root_share)} sub={`@ ${d.pct}% platform share`} testid="rev-share" delay={0.05} />
        <StatCard icon={ShieldCheck} label="Super Admin Plan Revenue" value={fmtINR(d.totals.plan_revenue)} sub="subscription fees" testid="rev-plan" delay={0.1} />
        <StatCard icon={Crown} label="Total Root Revenue" value={fmtINR(d.totals.total_root_revenue)} sub="share + plan fees" testid="rev-total" delay={0.15} />
      </div>

      <p className="overline-label mb-3">Revenue by Super Admin</p>
      <TableShell testid="rev-admins-table" head={["Super Admin", "Partners", "Partner Revenue", "Plan", "Plan Fee", `Share @ ${d.pct}%`, "Total to Root", ""]}>
        {d.admins.map((a) => (
          <Fragment key={a.id}>
            <TR testid={`rev-admin-${a.id}`}>
              <TD><div><p className="font-bold text-sm">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div></TD>
              <TD className="font-bold">{a.partners.length}</TD>
              <TD className="font-bold">{fmtINR(a.partner_revenue)}</TD>
              <TD><span className="text-xs font-bold capitalize px-2 py-1 rounded-full border border-border">{a.plan}</span></TD>
              <TD className="font-semibold">{a.status === "active" ? fmtINR(a.plan_fee) : "—"}</TD>
              <TD className="font-bold text-emerald-500">{fmtINR(a.share_amount)}</TD>
              <TD className="font-extrabold">{fmtINR(a.total_root_revenue)}</TD>
              <TD>
                <button data-testid={`rev-expand-${a.id}`} onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors">
                  {expanded === a.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </TD>
            </TR>
            {expanded === a.id && (
              <tr key={`${a.id}-partners`} data-testid={`rev-partners-${a.id}`}>
                <td colSpan={8} className="px-5 pb-4">
                  <div className="rounded-md border border-border bg-muted/30 p-4">
                    <p className="overline-label mb-2">Revenue by Partner — billed independently on each partner's own website count</p>
                    {a.partners.length === 0 ? <p className="text-sm text-muted-foreground">No partners yet.</p> : (
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-muted-foreground text-xs">
                          <th className="py-1.5 font-semibold">Partner</th><th className="font-semibold">Active Sites</th>
                          <th className="font-semibold">Monthly Billing</th><th className="font-semibold">Share @ {d.pct}%</th></tr></thead>
                        <tbody>
                          {a.partners.map((p) => (
                            <tr key={p.id} data-testid={`rev-partner-${p.id}`} className="border-t border-border/50">
                              <td className="py-2"><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.email}</p></td>
                              <td className="font-semibold">{p.active_websites}</td>
                              <td className="font-semibold">{fmtINR(p.monthly_amount)}</td>
                              <td className="font-semibold text-emerald-500">{fmtINR(p.share_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </TableShell>

      <p className="overline-label mb-3 mt-10 flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Monthly Revenue History</p>
      <TableShell testid="rev-monthly-table" head={["Month", "Partner Revenue (settled)", "% Applied", "Root Share", "Plan Fees", "Total Root Revenue"]}>
        {d.monthly.map((mo) => (
          <TR key={mo.month} testid={`rev-month-${mo.month.replace(/\s/g, "-")}`}>
            <TD className="font-bold text-sm">{mo.month}</TD>
            <TD className="font-semibold">{fmtINR(mo.partner_revenue)}</TD>
            <TD className="font-semibold">{mo.pct}%</TD>
            <TD className="font-bold text-emerald-500">{fmtINR(mo.share)}</TD>
            <TD className="font-semibold">{fmtINR(mo.plan_revenue)}</TD>
            <TD className="font-extrabold">{fmtINR(mo.total)}</TD>
          </TR>
        ))}
      </TableShell>

      <p className="overline-label mb-3 mt-10 flex items-center gap-2"><History className="h-4 w-4" /> Percentage Change History</p>
      {d.history.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="pct-history-empty">No changes yet — running at the default 10%.</p>
      ) : (
        <TableShell testid="pct-history-table" head={["Changed By", "Previous", "New", "Date / Time"]}>
          {d.history.map((h) => (
            <TR key={h.id} testid={`pct-history-${h.id}`}>
              <TD className="font-semibold text-sm">{h.changed_by}</TD>
              <TD className="font-semibold">{h.prev_pct}%</TD>
              <TD className="font-extrabold text-emerald-500">{h.new_pct}%</TD>
              <TD className="text-sm text-muted-foreground">{fmtDateTime(h.created_at)}</TD>
            </TR>
          ))}
        </TableShell>
      )}
    </div>
  );
}
